import * as React from 'react';
import { useIsMobileView, useTranslation } from '@front10/utils';
import { UseFormMethods } from 'react-hook-form';
import { Listbox } from './Listbox';
import { FlexibleFilterContainer } from './FlexibleFilterContainer';
import { FilterButton } from './FilterButton';
import { Panels } from '../containers/VerticalBookingContainer/Panels';
import { Box, Icon, Text } from '@front10/base-elements';

export interface ListSelectorProps {
  defaultValue?: string;
  selectedValue?: string;
  headerLabelKey?: string;
  doneButtonLabelKey?: string;
  noSelectedItemLabelKey?: string;
  fieldName: string;
  options: Array<{
    value: string;
    label: string;
    href?: string;
    rules?: Array<{}>;
  }>;
}

const ListSelector = ({
  fieldName,
  defaultValue,
  selectedValue,
  options,
  control,
  getValues,
  setValue,
  headerLabelKey = '',
  doneButtonLabelKey = 'cmp-list-selector-filter-done-button',
  noSelectedItemLabelKey = '',
  ...rest
}: ListSelectorProps &
  Pick<UseFormMethods<any>, 'control' | 'register' | 'getValues' | 'setValue'>) => {
  const translate = useTranslation();
  const isMobile = useIsMobileView();

  const selectedItem = React.useMemo(
    () => options.filter((opt) => opt.value === defaultValue || opt.value === selectedValue)[0],
    [defaultValue, options, selectedValue]
  );

  const activeFilter = React.useMemo(() => {
    return !!Boolean(getValues().carType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues()]);

  const MobileItem = ({ item }: { item: ListSelectorProps['options'][0] }) => {
    const itemSelected = item.value === selectedItem.value;

    return (
      <Box
        onClick={() => {
          setValue(fieldName, item.value);
        }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          variant: itemSelected ? 'buttons.selectedBase' : 'buttons.base'
        }}
      >
        <Text variant="text.fontSize5">{translate(item.label)}</Text>
        {itemSelected ? <Icon icon="check" size={20} sx={{ fill: 'primary' }} /> : null}
      </Box>
    );
  };

  const MobileLayout = (
    <FlexibleFilterContainer
      label={translate(headerLabelKey)}
      title={translate(headerLabelKey)}
      onSave={() => {}}
      saveOnClose
      toggleElement={<FilterButton active={activeFilter} value={translate(selectedItem.label)} />}
      labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
    >
      <Panels showSeparator>
        {options.map((elem) => (
          <MobileItem item={elem} />
        ))}
      </Panels>
    </FlexibleFilterContainer>
  );

  const DesktopLayout = (
    <Listbox
      label={translate(headerLabelKey)}
      defaultValue={defaultValue ?? selectedValue}
      options={options}
      onChange={(newValue) => {
        setValue(fieldName, newValue);
      }}
      labelNotSelectedItem={translate(noSelectedItemLabelKey, 'Elements')}
    />
  );

  return isMobile ? MobileLayout : DesktopLayout;
};

export { ListSelector };
